import type { AbilityDef, Content, EnemyDef, Modifier } from '@gra/content';
import { hazardSpeedFactor, updateMapFeatures } from './map-features';
import { obstaclesNear, pushOutOfObstacles, type Obstacle } from './obstacles';
import { updateTelegraphs } from './boss';
import { chargeImpact, enemyIntent, updateHostileShots } from './enemy-ai';
import { firstRunEvent, updateRunEvents } from './run-events';
import { collectAll, dropFromBoss, dropFromEnemy, updateChests, updateDrops } from './loot';
import { critMultiplier, effect, shieldDamage, THORNS_TICK, updateBarrier } from './effects';
import { checkLevelUp, refreshModifiers, xpForLevel } from './progression';
import { insideObstacle, pullLoot, scatterLoot } from './physics';
import { nextRandom } from './rng';
import { SpatialGrid } from './spatial';
import { applyModifiers, computeAbilityStats, computePlayerStats } from './stats';
import type { AbilityState, Enemy, GameState, Input } from './types';

const GRID_CELL = 64;
const PROJECTILE_SPREAD = 0.18;
const SPAWN_JITTER = 60;
/** Pułapka: czas uzbrajania i samoczynnego wybuchu (s), odstęp kilku pułapek z jednego rzutu. */
const TRAP_ARM = 0.35;
const TRAP_LIFE = 6;
const TRAP_SPREAD = 45;
/** Odstęp tików podpalenia (s). */
const BURN_TICK = 0.5;

/** Bufory wielokrotnego użytku dla zapytań (silnik jest jednowątkowy). */
const nearby: Enemy[] = [];
const blockers: Obstacle[] = [];

/** Ile przeszkód na kawałek mapy ma aktualna strefa. */
const obstacleCount = (s: GameState) => s.content.zones[s.zoneId]?.obstacles ?? 0;

/** @param loadout modyfikatory z ekwipunku (np. `equipmentModifiers(content, profile)`). */
export function createGame(
  content: Content,
  classId: string,
  seed: number,
  loadout: readonly Modifier[] = [],
  zoneId: string = Object.keys(content.zones)[0]!,
  raceId: string = Object.keys(content.races)[0]!,
  /** Moc ekwipunku (suma itemPower założonych przedmiotów) — porównywana z zalecaną mocą strefy. */
  power = 0,
): GameState {
  const race = content.races[raceId];
  if (!race) throw new Error(`Nieznana rasa: ${raceId}`);
  // Cechy rasowe stałe działają jak ekwipunek — przez całą wyprawę.
  loadout = [...loadout, ...race.modifiers];
  const cls = content.classes[classId];
  if (!cls) throw new Error(`Nieznana klasa: ${classId}`);
  if (!content.zones[zoneId]) throw new Error(`Nieznana strefa: ${zoneId}`);
  const stats = computePlayerStats(cls.baseStats, loadout);
  return {
    content,
    classId,
    zoneId,
    raceId,
    periodic: race.periodic.map((p) => ({ timer: p.every * 0.5, left: 0 })),
    seed,
    maxEnemyRadius: Math.max(...Object.values(content.enemies).map((e) => e.radius)),
    rng: seed | 0,
    time: 0,
    nextId: 1,
    player: { x: 0, y: 0, vx: 0, vy: 0, hp: stats.maxHealth, stats, shield: 0, shieldTimer: 0, thornsTimer: THORNS_TICK },
    enemies: [],
    projectiles: [],
    traps: [],
    hostileShots: [],
    gems: [],
    drops: [],
    chests: [],
    chestTimer: content.game.loot.firstChest,
    broken: [],
    hazardTimer: 0,
    gold: 0,
    loot: [],
    relics: [],
    abilities: cls.startingAbilities.map((id) => ({ id, cooldownLeft: 0.5, rank: 1 })),
    talents: {},
    loadout,
    modifiers: [...loadout],
    level: 1,
    xp: 0,
    xpToNext: xpForLevel(content, 1),
    kills: 0,
    damageDealt: 0,
    spawnTimer: 1,
    pendingChoices: null,
    over: false,
    won: false,
    defeatedBosses: [],
    bossesSpawned: 0,
    bossIds: [],
    telegraphs: [],
    power,
    powerPenalty: powerPenalty(content, zoneId, power),
    runEvent: null,
    runEventTimer: firstRunEvent({ content }),
    events: [],
  };
}

/** Mnożnik zdrowia wrogów, gdy moc ekwipunku jest niższa od zalecanej dla strefy (1 = bez kary). */
export function powerPenalty(content: Content, zoneId: string, power: number): number {
  const recommended = content.zones[zoneId]?.recommendedPower ?? 0;
  if (recommended <= 0 || power >= recommended) return 1;
  const { penaltyPerMissing, maxPenalty } = content.game.power;
  return 1 + Math.min(maxPenalty, ((recommended - power) / recommended) * penaltyPerMissing);
}

/** Jeden krok symulacji o stałej długości `dt` sekund. */
export function step(s: GameState, input: Input, dt: number): void {
  s.events = [];
  if (s.over || s.pendingChoices) return;
  s.time += dt;

  updatePlayer(s, input, dt);
  updatePeriodic(s, dt);
  updateBarrier(s, dt);
  const grid = new SpatialGrid<Enemy>(GRID_CELL);
  for (const e of s.enemies) grid.insert(e);
  updateAbilities(s, grid, dt);
  updateProjectiles(s, grid, dt);
  updateTraps(s, grid, dt);
  updateMapFeatures(s, dt);
  updateEnemies(s, grid, dt);
  updateTelegraphs(s, dt);
  updateRunEvents(s, dt);
  updateHostileShots(s, dt);
  updateGems(s, dt);
  updateDrops(s, dt);
  updateChests(s, dt);
  spawnEnemies(s, dt);
  spawnBosses(s);

  s.enemies = s.enemies.filter((e) => e.hp > 0);
  s.bossIds = s.bossIds.filter((id) => s.enemies.some((e) => e.id === id));
  s.projectiles = s.projectiles.filter((p) => p.life > 0 && p.pierceLeft >= 0);
  s.traps = s.traps.filter((t) => t.life > 0);

  if (s.won) {
    s.over = true;
    return;
  }
  if (s.player.hp <= 0) {
    s.player.hp = 0;
    s.over = true;
    s.events.push({ type: 'gameOver' });
    return;
  }
  checkLevelUp(s);
}

/** Zdolności okresowe rasy: włączają się co jakiś czas na kilka sekund (np. +30% obrażeń na 10 s). */
function updatePeriodic(s: GameState, dt: number): void {
  const defs = s.content.races[s.raceId]?.periodic ?? [];
  let changed = false;
  s.periodic.forEach((p, i) => {
    const def = defs[i];
    if (!def) return;
    if (p.left > 0) {
      p.left -= dt;
      if (p.left <= 0) {
        p.left = 0;
        changed = true;
      }
    }
    p.timer -= dt;
    if (p.timer <= 0) {
      p.timer = def.every;
      p.left = def.duration;
      changed = true;
      s.events.push({ type: 'racial', name: def.name, duration: def.duration });
    }
  });
  if (changed) refreshModifiers(s);
}

function updatePlayer(s: GameState, input: Input, dt: number): void {
  const p = s.player;
  const phys = s.content.game.physics;
  // Docelowa prędkość z joysticka/klawiszy (wychylenie < 1 = wolniej); bohater dochodzi do niej z przyspieszeniem.
  const len = Math.hypot(input.moveX, input.moveY);
  const speed = p.stats.moveSpeed * hazardSpeedFactor(s);
  const k = len > 0 ? speed / Math.max(1, len) : 0;
  const tx = input.moveX * k;
  const ty = input.moveY * k;
  const dvx = tx - p.vx;
  const dvy = ty - p.vy;
  const dv = Math.hypot(dvx, dvy);
  if (dv > 0) {
    // Ruszanie i skręt szybkie, hamowanie trochę łagodniejsze — bez efektu lodu.
    const step = Math.min(dv, (len > 0 ? phys.playerAccel : phys.playerDecel) * dt);
    p.vx += (dvx / dv) * step;
    p.vy += (dvy / dv) * step;
  }
  p.x += p.vx * dt;
  p.y += p.vy * dt;
  // Drzewa, groby i skały blokują drogę — bohater ślizga się po ich krawędzi.
  pushOutOfObstacles(p, s.content.game.playerRadius, obstaclesNear(s.seed, obstacleCount(s), p.x, p.y, s.content.game.playerRadius, blockers));
  p.hp = Math.min(p.stats.maxHealth, p.hp + p.stats.regen * dt);
}

/** @param abilityId umiejętność, która trafiła — jej `status` (podpalenie itd.) trafia na przeżywającego wroga. */
function damageEnemy(s: GameState, e: Enemy, base: number, abilityId?: string): void {
  if (e.hp <= 0) return;
  const crit = nextRandom(s) < s.player.stats.critChance;
  const amount = crit ? base * critMultiplier(s) : base;
  e.hp -= amount;
  // Dobijanie (zwieńczenie Ataku, pierścień kata): słabi wrogowie padają od razu; bossowie są odporni.
  if (e.hp > 0 && e.hp < e.maxHp * effect(s, 'executeBelow') && s.content.enemies[e.defId]?.behavior !== 'boss') e.hp = 0;
  s.damageDealt += amount;
  e.hitFlash = 0.1;
  s.events.push({ type: 'enemyHit', id: e.id, x: e.x, y: e.y, amount, crit });
  if (e.hp <= 0) {
    s.kills += 1;
    const heal = effect(s, 'lifeOnKill');
    if (heal > 0) s.player.hp = Math.min(s.player.stats.maxHealth, s.player.hp + heal);
    s.gems.push({ id: s.nextId++, x: e.x, y: e.y, value: e.xp, magnet: false });
    s.events.push({ type: 'enemyDied', id: e.id, defId: e.defId, x: e.x, y: e.y });
    dropFromEnemy(s, e);
    if (s.content.enemies[e.defId]?.behavior === 'boss') bossDefeated(s, e);
    return;
  }
  const status = abilityId ? s.content.abilities[abilityId]?.status : undefined;
  if (status) applyStatus(s, e, status);
}

function applyStatus(s: GameState, e: Enemy, status: NonNullable<AbilityDef['status']>): void {
  const boss = s.content.enemies[e.defId]?.behavior === 'boss';
  let fresh = false;
  if (status.kind === 'burn') {
    fresh = !e.burn;
    e.burn = { dps: Math.max(status.power, e.burn?.dps ?? 0), left: Math.max(status.duration, e.burn?.left ?? 0), tick: e.burn?.tick ?? BURN_TICK };
  } else if (status.kind === 'slow') {
    const power = Math.min(0.9, boss ? status.power / 2 : status.power);
    fresh = !e.slow;
    e.slow = { power: Math.max(power, e.slow?.power ?? 0), left: Math.max(status.duration, e.slow?.left ?? 0) };
  } else {
    if (boss) return;
    fresh = !(e.stun && e.stun > 0);
    e.stun = Math.max(status.duration, e.stun ?? 0);
  }
  if (fresh) s.events.push({ type: 'status', id: e.id, kind: status.kind, x: e.x, y: e.y });
}

/** Odliczanie efektów na wrogu; podpalenie zadaje obrażenia co pół sekundy. */
function tickStatuses(s: GameState, e: Enemy, dt: number): void {
  if (e.burn) {
    e.burn.left -= dt;
    e.burn.tick -= dt;
    if (e.burn.tick <= 0) {
      e.burn.tick += BURN_TICK;
      damageEnemy(s, e, e.burn.dps * BURN_TICK);
    }
    if (e.burn && e.burn.left <= 0) delete e.burn;
  }
  if (e.slow) {
    e.slow.left -= dt;
    if (e.slow.left <= 0) delete e.slow;
  }
  if (e.stun !== undefined) {
    e.stun -= dt;
    if (e.stun <= 0) delete e.stun;
  }
}

/** Odpycha wroga od gracza. Ciężcy (duży promień) lecą mniej. */
function knockBack(s: GameState, e: Enemy, amount: number): void {
  if (amount === 0 || e.hp <= 0) return;
  const dx = e.x - s.player.x;
  const dy = e.y - s.player.y;
  const d = Math.hypot(dx, dy) || 1;
  let push = amount * Math.min(1, 14 / e.radius);
  // Przyciąganie (ujemny odrzut) kończy się na styku z bohaterem — wróg nie wpada w niego.
  if (push < 0) push = -Math.min(-push, Math.max(0, d - s.content.game.playerRadius - e.radius));
  // Odrzut i przyciąganie jako prędkość: wróg przelatuje tę samą drogę, ale płynnie, z wytłumieniem.
  const v = push * s.content.game.physics.knockbackDamping;
  let kvx = (e.kvx ?? 0) + (dx / d) * v;
  let kvy = (e.kvy ?? 0) + (dy / d) * v;
  const kv = Math.hypot(kvx, kvy);
  const max = s.content.game.physics.maxKnockbackSpeed;
  if (kv > max) {
    kvx *= max / kv;
    kvy *= max / kv;
  }
  e.kvx = kvx;
  e.kvy = kvy;
}

/** Lot po odrzucie; zwraca, ile własnego ruchu wróg zachowuje (0 = leci bezwładnie, 1 = pełna kontrola). */
function applyKnockback(s: GameState, e: Enemy, dt: number): number {
  if (e.kvx === undefined || e.kvy === undefined) return 1;
  const phys = s.content.game.physics;
  e.x += e.kvx * dt;
  e.y += e.kvy * dt;
  const decay = Math.exp(-phys.knockbackDamping * dt);
  e.kvx *= decay;
  e.kvy *= decay;
  const kv = Math.hypot(e.kvx, e.kvy);
  if (kv < 4) {
    delete e.kvx;
    delete e.kvy;
    return 1;
  }
  const limit = Math.max(1, e.speed * phys.staggerRatio);
  return Math.max(0, 1 - kv / (limit * 2));
}

function nearestEnemy(s: GameState, grid: SpatialGrid<Enemy>, range: number): Enemy | null {
  const p = s.player;
  let best: Enemy | null = null;
  let bestD2 = range * range;
  for (const e of grid.queryRadius(p.x, p.y, range, nearby)) {
    if (e.hp <= 0) continue;
    const d2 = (e.x - p.x) ** 2 + (e.y - p.y) ** 2;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = e;
    }
  }
  return best;
}

function updateAbilities(s: GameState, grid: SpatialGrid<Enemy>, dt: number): void {
  const p = s.player;
  for (const a of s.abilities) {
    a.cooldownLeft -= dt;
    if (a.cooldownLeft > 0) continue;
    const def = s.content.abilities[a.id];
    if (!def) continue;
    const st = computeAbilityStats(def, s.modifiers, a.rank);

    if (def.kind === 'projectile') {
      const target = nearestEnemy(s, grid, st.range);
      if (!target) {
        a.cooldownLeft = 0; // gotowe do strzału, gdy tylko ktoś wejdzie w zasięg
        continue;
      }
      const base = Math.atan2(target.y - p.y, target.x - p.x);
      s.events.push({ type: 'abilityUsed', abilityId: def.id, targetX: target.x, targetY: target.y });
      for (let i = 0; i < st.projectileCount; i++) {
        const angle = base + (i - (st.projectileCount - 1) / 2) * PROJECTILE_SPREAD;
        s.projectiles.push({
          id: s.nextId++,
          abilityId: def.id,
          x: p.x,
          y: p.y,
          vx: Math.cos(angle) * st.projectileSpeed,
          vy: Math.sin(angle) * st.projectileSpeed,
          damage: st.damage,
          radius: st.area,
          pierceLeft: st.pierce,
          life: st.range / st.projectileSpeed,
          hitIds: [],
        });
      }
    } else if (def.kind === 'melee') {
      // Cios wymaga celu w zasięgu — inaczej czekamy, aż ktoś podejdzie.
      const target = nearestEnemy(s, grid, st.area + s.maxEnemyRadius);
      if (!target) {
        a.cooldownLeft = 0;
        continue;
      }
      const angle = Math.atan2(target.y - p.y, target.x - p.x);
      for (const e of grid.queryRadius(p.x, p.y, st.area + s.maxEnemyRadius, nearby)) {
        const dx = e.x - p.x;
        const dy = e.y - p.y;
        if (dx * dx + dy * dy > (st.area + e.radius) ** 2) continue;
        const diff = Math.abs(Math.atan2(Math.sin(Math.atan2(dy, dx) - angle), Math.cos(Math.atan2(dy, dx) - angle)));
        // Wróg tuż przy postaci dostaje zawsze, nawet z boku łuku.
        if (diff > st.arc / 2 && dx * dx + dy * dy > (e.radius + s.content.game.playerRadius) ** 2) continue;
        damageEnemy(s, e, st.damage, def.id);
        knockBack(s, e, st.knockback);
      }
      s.events.push({ type: 'abilityUsed', abilityId: def.id, targetX: target.x, targetY: target.y });
      s.events.push({ type: 'slash', abilityId: def.id, x: p.x, y: p.y, angle, radius: st.area, arc: st.arc });
    } else if (def.kind === 'orbit') {
      // Ostrza krążą cały czas (pozycje liczone z czasu gry), a co `cooldown` ranią to, czego dotykają.
      const hit = new Set<number>();
      for (const b of orbitBlades(s, a)) {
        for (const e of grid.queryRadius(b.x, b.y, st.area + s.maxEnemyRadius, nearby)) {
          if (hit.has(e.id) || (e.x - b.x) ** 2 + (e.y - b.y) ** 2 > (st.area + e.radius) ** 2) continue;
          hit.add(e.id);
          damageEnemy(s, e, st.damage, def.id);
          knockBack(s, e, st.knockback);
        }
      }
    } else if (def.kind === 'aura') {
      for (const e of grid.queryRadius(p.x, p.y, st.area + s.maxEnemyRadius, nearby)) {
        if ((e.x - p.x) ** 2 + (e.y - p.y) ** 2 > (st.area + e.radius) ** 2) continue;
        damageEnemy(s, e, st.damage, def.id);
        knockBack(s, e, st.knockback);
      }
      s.events.push({ type: 'aura', abilityId: def.id, x: p.x, y: p.y, radius: st.area });
    } else if (def.kind === 'chain') {
      const first = nearestEnemy(s, grid, st.range);
      if (!first) {
        a.cooldownLeft = 0;
        continue;
      }
      const points = [{ x: p.x, y: p.y }];
      const hit = new Set<number>();
      let current: Enemy | null = first;
      for (let jump = 0; current && jump <= st.projectileCount; jump++) {
        hit.add(current.id);
        points.push({ x: current.x, y: current.y });
        damageEnemy(s, current, st.damage, def.id);
        // Następny cel: najbliższy jeszcze nietrafiony wróg w zasięgu przeskoku.
        let next: Enemy | null = null;
        let bestD2 = st.area * st.area;
        for (const e of grid.queryRadius(current.x, current.y, st.area, nearby)) {
          if (e.hp <= 0 || hit.has(e.id)) continue;
          const d2 = (e.x - current.x) ** 2 + (e.y - current.y) ** 2;
          if (d2 < bestD2) {
            bestD2 = d2;
            next = e;
          }
        }
        current = next;
      }
      s.events.push({ type: 'abilityUsed', abilityId: def.id, targetX: first.x, targetY: first.y });
      s.events.push({ type: 'chain', abilityId: def.id, points });
    } else if (def.kind === 'trap') {
      // Pułapka ląduje pod najbliższym wrogiem w zasięgu rzutu, a gdy nikogo nie ma — pod postacią.
      const target = nearestEnemy(s, grid, st.range);
      const cx = target?.x ?? p.x;
      const cy = target?.y ?? p.y;
      for (let i = 0; i < st.projectileCount; i++) {
        const angle = (i / st.projectileCount) * Math.PI * 2;
        const off = st.projectileCount > 1 ? TRAP_SPREAD : 0;
        const trap = { id: s.nextId++, abilityId: def.id, x: cx + Math.cos(angle) * off, y: cy + Math.sin(angle) * off, damage: st.damage, radius: st.area, arm: TRAP_ARM, life: TRAP_LIFE };
        s.traps.push(trap);
        s.events.push({ type: 'trapPlaced', id: trap.id, abilityId: def.id, x: trap.x, y: trap.y });
      }
      s.events.push({ type: 'abilityUsed', abilityId: def.id, targetX: cx, targetY: cy });
    } else {
      for (const e of grid.queryRadius(p.x, p.y, st.area + s.maxEnemyRadius, nearby)) {
        if ((e.x - p.x) ** 2 + (e.y - p.y) ** 2 > (st.area + e.radius) ** 2) continue;
        damageEnemy(s, e, st.damage, def.id);
        knockBack(s, e, st.knockback);
      }
      s.events.push({ type: 'abilityUsed', abilityId: def.id, targetX: p.x, targetY: p.y });
      s.events.push({ type: 'nova', abilityId: def.id, x: p.x, y: p.y, radius: st.area });
    }
    a.cooldownLeft = st.cooldown;
  }
}

/**
 * Pozycje ostrzy umiejętności typu orbit — zależą tylko od czasu gry, więc render rysuje je
 * w tych samych miejscach, w których silnik liczy trafienia.
 */
export function orbitBlades(s: GameState, a: AbilityState): { x: number; y: number; angle: number }[] {
  const def = s.content.abilities[a.id];
  if (!def || def.kind !== 'orbit') return [];
  const st = computeAbilityStats(def, s.modifiers, a.rank);
  const base = (s.time * st.projectileSpeed) / st.range;
  const out: { x: number; y: number; angle: number }[] = [];
  for (let i = 0; i < st.projectileCount; i++) {
    const angle = base + (i / st.projectileCount) * Math.PI * 2;
    out.push({ x: s.player.x + Math.cos(angle) * st.range, y: s.player.y + Math.sin(angle) * st.range, angle });
  }
  return out;
}

/** Pułapki: uzbrajają się, wybuchają pod wrogiem albo same po czasie. */
function updateTraps(s: GameState, grid: SpatialGrid<Enemy>, dt: number): void {
  for (const t of s.traps) {
    t.arm -= dt;
    t.life -= dt;
    let burst = t.life <= 0;
    if (!burst && t.arm <= 0) {
      for (const e of grid.queryRadius(t.x, t.y, t.radius * 0.5 + s.maxEnemyRadius, nearby)) {
        if (e.hp > 0 && (e.x - t.x) ** 2 + (e.y - t.y) ** 2 <= (t.radius * 0.5 + e.radius) ** 2) {
          burst = true;
          break;
        }
      }
    }
    if (!burst) continue;
    for (const e of grid.queryRadius(t.x, t.y, t.radius + s.maxEnemyRadius, nearby)) {
      if ((e.x - t.x) ** 2 + (e.y - t.y) ** 2 > (t.radius + e.radius) ** 2) continue;
      damageEnemy(s, e, t.damage, t.abilityId);
    }
    s.events.push({ type: 'trapBurst', id: t.id, abilityId: t.abilityId, x: t.x, y: t.y, radius: t.radius });
    t.life = 0;
  }
}

function updateProjectiles(s: GameState, grid: SpatialGrid<Enemy>, dt: number): void {
  const hitsRocks = s.content.game.physics.playerShotsHitObstacles;
  for (const pr of s.projectiles) {
    pr.x += pr.vx * dt;
    pr.y += pr.vy * dt;
    pr.life -= dt;
    if (pr.life <= 0) continue;
    if (hitsRocks && insideObstacle(s, pr.x, pr.y)) {
      pr.life = 0;
      s.events.push({ type: 'shotBlocked', x: pr.x, y: pr.y, abilityId: pr.abilityId });
      continue;
    }
    for (const e of grid.queryRadius(pr.x, pr.y, pr.radius + s.maxEnemyRadius, nearby)) {
      if (e.hp <= 0 || pr.hitIds.includes(e.id)) continue;
      if ((e.x - pr.x) ** 2 + (e.y - pr.y) ** 2 > (pr.radius + e.radius) ** 2) continue;
      damageEnemy(s, e, pr.damage, pr.abilityId);
      pr.hitIds.push(e.id);
      pr.pierceLeft -= 1;
      if (pr.pierceLeft < 0) break;
    }
  }
}

function updateEnemies(s: GameState, grid: SpatialGrid<Enemy>, dt: number): void {
  const p = s.player;
  const playerRadius = s.content.game.playerRadius;
  const armorFactor = 100 / (100 + p.stats.armor);
  let hurt = 0;
  const touching: Enemy[] = [];

  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    e.hitFlash = Math.max(0, e.hitFlash - dt);
    tickStatuses(s, e, dt);
    if (e.hp <= 0) continue; // spłonął
    const control = applyKnockback(s, e, dt);
    if (e.stun) {
      // Ogłuszony stoi i nie atakuje, ale odrzut nadal go niesie — bez wchodzenia w przeszkody.
      if (control < 1) pushOutOfObstacles(e, e.radius, obstaclesNear(s.seed, obstacleCount(s), e.x, e.y, e.radius, blockers));
      continue;
    }
    const dx = p.x - e.x;
    const dy = p.y - e.y;
    const dist = Math.hypot(dx, dy) || 1;
    const def = s.content.enemies[e.defId];
    const intent = def ? enemyIntent(s, e, def, dx, dy, dist, dt) : { vx: (dx / dist) * e.speed, vy: (dy / dist) * e.speed };
    if (e.hp <= 0) continue; // wybuchł
    const slowK = (e.slow ? 1 - e.slow.power : 1) * control;
    let vx = intent.vx * slowK;
    let vy = intent.vy * slowK;

    // Rozpychanie się wrogów, żeby nie zlewali się w jeden punkt: miękka siła plus częściowa korekta pozycji.
    const phys = s.content.game.physics;
    let cx = 0;
    let cy = 0;
    let sx = 0;
    let sy = 0;
    for (const o of grid.queryRadius(e.x, e.y, (e.radius + s.maxEnemyRadius) * phys.personalSpace, nearby)) {
      if (o === e || o.hp <= 0) continue;
      const ox = e.x - o.x;
      const oy = e.y - o.y;
      const od = Math.hypot(ox, oy);
      const min = (e.radius + o.radius) * phys.personalSpace;
      if (od > 0 && od < min) {
        // Masa rośnie z powierzchnią: ciężki sąsiad pcha mocno, lekki ledwo przesuwa ciężkiego.
        const share = (o.radius * o.radius) / (e.radius * e.radius + o.radius * o.radius);
        const weight = Math.min(1.8, 2 * share);
        const push = ((min - od) / min) * Math.max(e.speed, 40) * phys.separation * weight;
        sx += (ox / od) * push;
        sy += (oy / od) * push;
        cx += (ox / od) * (min - od) * share * phys.crowdCorrection;
        cy += (oy / od) * (min - od) * share * phys.crowdCorrection;
      }
    }
    // Siła i korekta ograniczone, żeby w gęstym tłumie wróg nie przeskakiwał o kilka kroków naraz.
    const sl = Math.hypot(sx, sy);
    const maxS = Math.max(e.speed, 40) * phys.separation * 1.8;
    if (sl > maxS) {
      sx *= maxS / sl;
      sy *= maxS / sl;
    }
    vx += sx;
    vy += sy;
    const cl = Math.hypot(cx, cy);
    const maxC = 90 * dt;
    if (cl > maxC) {
      cx *= maxC / cl;
      cy *= maxC / cl;
    }
    e.x += cx;
    e.y += cy;
    // Omijanie przeszkód: gdy drzewo stoi na drodze, wróg skręca bokiem zamiast utknąć.
    const near = obstaclesNear(s.seed, obstacleCount(s), e.x, e.y, e.radius + 40, blockers);
    for (const o of near) {
      const ox = o.x - e.x;
      const oy = o.y - e.y;
      const od = Math.hypot(ox, oy) || 1;
      const ahead = (ox * vx + oy * vy) / (od * Math.max(e.speed, Math.hypot(vx, vy), 1));
      if (ahead <= 0.2 || od > o.radius + e.radius + 40) continue;
      // Skręt w tę stronę, po której jest gracz.
      const side = Math.sign(ox * dy - oy * dx) || 1;
      const k = ahead * e.speed * 1.2;
      vx += (oy / od) * side * k;
      vy += (-ox / od) * side * k;
    }
    e.x += vx * dt;
    e.y += vy * dt;
    pushOutOfObstacles(e, e.radius, near);

    // Wróg i bohater nie przenikają się: wróg jest wypychany, a bohater lekko odpychany przez napór.
    const px = e.x - p.x;
    const py = e.y - p.y;
    const pd = Math.hypot(px, py);
    const min = playerRadius + e.radius;
    if (pd < min) {
      const overlap = min - (pd || 0.001);
      const nx = pd > 0.001 ? px / pd : 1;
      const ny = pd > 0.001 ? py / pd : 0;
      const heavy = Math.min(0.45, e.radius / 60);
      e.x += nx * overlap * (1 - heavy);
      e.y += ny * overlap * (1 - heavy);
      p.x -= nx * overlap * heavy;
      p.y -= ny * overlap * heavy;
    }

    // Kontakt = stykanie się (z małym zapasem, bo ciała się już nie nakładają).
    if (Math.hypot(e.x - p.x, e.y - p.y) < playerRadius + e.radius + 4) {
      hurt += e.damage * dt * armorFactor;
      touching.push(e);
      if (def) chargeImpact(s, e, def);
    }
  }
  // Kolce: co pół sekundy ranią wszystkich, którzy dotykają bohatera.
  p.thornsTimer -= dt;
  if (p.thornsTimer <= 0) {
    p.thornsTimer = THORNS_TICK;
    const thorns = effect(s, 'thorns') * THORNS_TICK;
    if (thorns > 0) for (const e of touching) damageEnemy(s, e, thorns);
  }
  // Napór wrogów nie może wepchnąć bohatera w drzewo.
  pushOutOfObstacles(p, playerRadius, obstaclesNear(s.seed, obstacleCount(s), p.x, p.y, playerRadius, blockers));

  if (hurt > 0) hurt = shieldDamage(s, hurt, false);
  if (hurt > 0) {
    p.hp -= hurt;
    s.events.push({ type: 'playerHurt', amount: hurt });
  }
}

function updateGems(s: GameState, dt: number): void {
  const p = s.player;
  const collectRadius = s.content.game.playerRadius + 6;
  for (const g of s.gems) {
    scatterLoot(s, g, dt);
    const d = Math.hypot(p.x - g.x, p.y - g.y);
    if (d <= collectRadius) {
      s.xp += g.value * applyModifiers(1, 'xpGain', s.modifiers);
      g.value = 0;
      continue;
    }
    if (d <= p.stats.pickupRadius) g.magnet = true;
    if (g.magnet) pullLoot(s, g, dt);
  }
  s.gems = s.gems.filter((g) => g.value > 0);
}

/** Bossowie strefy pojawiają się o ustalonych porach, niezależnie od limitu wrogów. */
function spawnBosses(s: GameState): void {
  const zone = s.content.zones[s.zoneId]!;
  const next = zone.bosses[s.bossesSpawned];
  if (!next || s.time < next.at) return;
  s.bossesSpawned += 1;
  const def = s.content.enemies[next.enemy];
  if (!def) return;
  const angle = nextRandom(s) * Math.PI * 2;
  const dist = s.content.game.spawn.ringDistance * 0.75;
  const hp = def.health * zone.healthMultiplier * (1 + (s.content.game.enemyHealthScalingPerMinute * s.time) / 120) * s.powerPenalty;
  const boss: Enemy = {
    id: s.nextId++,
    defId: def.id,
    x: s.player.x + Math.cos(angle) * dist,
    y: s.player.y + Math.sin(angle) * dist,
    hp,
    maxHp: hp,
    speed: def.speed,
    damage: def.damage,
    radius: def.radius,
    xp: def.xp,
    hitFlash: 0,
    timer: 2.5,
  };
  s.enemies.push(boss);
  s.bossIds.push(boss.id);
  s.events.push({ type: 'bossSpawned', id: boss.id, defId: def.id, final: next.final });
}

function bossDefeated(s: GameState, e: Enemy): void {
  const zone = s.content.zones[s.zoneId]!;
  const final = zone.bosses.some((b) => b.final && b.enemy === e.defId) && s.bossesSpawned >= zone.bosses.length;
  dropFromBoss(s, e);
  s.defeatedBosses.push(e.defId);
  s.events.push({ type: 'bossDefeated', id: e.id, defId: e.defId, x: e.x, y: e.y, final });
  if (final) {
    collectAll(s);
    s.won = true;
    s.events.push({ type: 'victory' });
  }
}

function pickEnemyDef(s: GameState): EnemyDef | null {
  const zone = s.content.zones[s.zoneId]!;
  const available = zone.enemies
    .map((z) => ({ def: s.content.enemies[z.enemy]!, weight: z.weight }))
    .filter((e) => e.def.appearsAt <= s.time && e.def.behavior !== 'boss');
  const total = available.reduce((sum, e) => sum + e.weight, 0);
  let roll = nextRandom(s) * total;
  for (const e of available) {
    roll -= e.weight;
    if (roll <= 0) return e.def;
  }
  return available[available.length - 1]?.def ?? null;
}

function spawnEnemies(s: GameState, dt: number): void {
  const cfg = s.content.game.spawn;
  s.spawnTimer -= dt;
  while (s.spawnTimer <= 0) {
    const ramp = Math.min(1, s.time / cfg.rampSeconds);
    s.spawnTimer += cfg.baseInterval + (cfg.minInterval - cfg.baseInterval) * ramp;
    const groupSize = Math.min(1 + Math.floor(s.time / cfg.groupGrowthSeconds), cfg.maxEnemies - s.enemies.length);
    if (groupSize <= 0) continue;

    const def = pickEnemyDef(s);
    if (!def) continue;
    const angle = nextRandom(s) * Math.PI * 2;
    const cx = s.player.x + Math.cos(angle) * cfg.ringDistance;
    const cy = s.player.y + Math.sin(angle) * cfg.ringDistance;
    const zone = s.content.zones[s.zoneId]!;
    const hp = def.health * zone.healthMultiplier * (1 + (s.content.game.enemyHealthScalingPerMinute * s.time) / 60) * s.powerPenalty;
    for (let i = 0; i < groupSize; i++) {
      s.enemies.push({
        id: s.nextId++,
        defId: def.id,
        x: cx + (nextRandom(s) - 0.5) * SPAWN_JITTER,
        y: cy + (nextRandom(s) - 0.5) * SPAWN_JITTER,
        hp,
        maxHp: hp,
        speed: def.speed,
        damage: def.damage,
        radius: def.radius,
        xp: def.xp,
        hitFlash: 0,
      });
    }
  }
}
